import axios from 'axios';

// Types for blog data
export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  imageUrl?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  socialImage?: string | null;
}

export interface BlogCategoryDetail {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeywords: string | null;
  socialImage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogCategoryResponse {
  categories: BlogCategory[];
  pagination: BlogPagination;
}

export interface BlogCategoryDetailResponse {
  category: BlogCategoryDetail;
  posts: BlogPost[];
  pagination: BlogPagination;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage: string;
  publishedAt: string;
  isFeatured: boolean;
  authorName: string | null;
  tags: string[];
  category: BlogCategory;
}

export interface BlogAuthor {
  id: string;
  firstName: string;
  lastName: string;
}

export interface BlogFAQ {
  id: string;
  blogId: string;
  question: string;
  answer: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface BlogPostDetail {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  categoryId: string;
  authorId: string;
  status: string;
  publishedAt: string;
  viewCount: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  authorEmail: string;
  highlights: string;
  tags: string[];
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  socialImage: string;
  seoSchema: string | null;
  isActive: boolean;
  allowComments: boolean;
  category: BlogCategory;
  author: BlogAuthor;
  blog_faqs: BlogFAQ[];
  relatedBlogs: BlogPost[];
}

export interface BlogPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface BlogResponse {
  status: string;
  data: {
    posts: BlogPost[];
    pagination: BlogPagination;
  };
}

// Create axios instance with base configuration
const blogApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public blog endpoints — no user_id query param needed
blogApi.interceptors.request.use(
  (config) => config,
  (error) => {
    // Request error occurred
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
blogApi.interceptors.response.use(
  (response) => {
    // Response received from API
    return response;
  },
  (error) => {
    // Response error occurred
    return Promise.reject(error);
  }
);

// Server-side data fetching functions (for SSR)
export const blogServiceServer = {
  // Get blog posts with pagination (server-side)
  async getBlogPosts(page: number = 1, limit: number = 10): Promise<BlogResponse> {
    try {
      const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;
      const response = await fetch(`${baseURL}/blogs/posts?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Add cache revalidation
        next: { revalidate: 3600 }, // Revalidate every hour
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching blog posts (server):', error);
      throw error;
    }
  },

  // Get blog posts by category (server-side)
  async getBlogPostsByCategory(categorySlug: string, page: number = 1, limit: number = 10): Promise<BlogResponse> {
    try {
      const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;
      const response = await fetch(`${baseURL}/blogs/posts?category=${categorySlug}&page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        next: { revalidate: 3600 },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching blog posts by category (server):', error);
      throw error;
    }
  },

  // Search blog posts (server-side)
  async searchBlogPosts(query: string, page: number = 1, limit: number = 18): Promise<BlogResponse> {
    try {
      const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;
      const response = await fetch(`${baseURL}/blogs/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        next: { revalidate: 3600 },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching blog posts (server):', error);
      throw error;
    }
  },

  // Get all blog categories (server-side)
  async getBlogCategories(page: number = 1, limit: number = 10): Promise<BlogCategoryResponse> {
    try {
      const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;
      const response = await fetch(`${baseURL}/blogs/categories?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        next: { revalidate: 3600 },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching blog categories (server):', error);
      throw error;
    }
  },

  // Get single blog post by slug (server-side with caching)
  async getBlogPostBySlug(slug: string): Promise<BlogPostDetail> {
    const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;
    const timeout = 8000; // 8 seconds timeout (reduced from 10s)
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(`${baseURL}/blogs/posts/slug/${slug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        // Cache for 1 hour to improve performance
        // Using next.revalidate ensures Next.js caches the response properly
        next: { revalidate: 3600 },
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        // If 404, return null instead of throwing to allow graceful handling
        if (response.status === 404) {
          return null as unknown as BlogPostDetail;
        }
        // If 503, throw a specific error that we can catch
        if (response.status === 503) {
          throw new Error('Service temporarily unavailable');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle timeout and network errors gracefully
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error(`Timeout fetching blog post by slug (server): ${slug}`);
          throw new Error('Request timeout - API took too long to respond');
        }
        if (error.message.includes('fetch') || error.message.includes('Network')) {
          console.error(`Network error fetching blog post by slug (server): ${slug}`, error);
          throw new Error('Network error - unable to reach API');
        }
        if (error.message.includes('Service temporarily unavailable')) {
          console.error(`503 error fetching blog post by slug (server): ${slug}`);
          throw new Error('Service temporarily unavailable');
        }
      }
      
      console.error('Error fetching blog post by slug (server):', error);
      throw error;
    }
  },
};

// Blog service functions (client-side)
export const blogService = {
  // Get blog posts with pagination
  async getBlogPosts(page: number = 1, limit: number = 10): Promise<BlogResponse> {
    try {
      const response = await blogApi.get(`/blogs/posts?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }
  },

  // Get single blog post by slug
  async getBlogPostBySlug(slug: string): Promise<BlogPostDetail> {
    try {
      const response = await blogApi.get(`/blogs/posts/slug/${slug}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      throw error;
    }
  },

  // Get featured blog posts
  async getFeaturedBlogPosts(limit: number = 5): Promise<BlogPost[]> {
    try {
      const response = await blogApi.get(`/blogs/posts?featured=true&limit=${limit}`);
      return response.data.data.posts;
    } catch (error) {
      console.error('Error fetching featured blog posts:', error);
      throw error;
    }
  },

  // Get blog posts by category
  async getBlogPostsByCategory(categorySlug: string, page: number = 1, limit: number = 10): Promise<BlogResponse> {
    try {
      const response = await blogApi.get(`/blogs/posts?category=${categorySlug}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog posts by category:', error);
      throw error;
    }
  },

  // Search blog posts using the new search endpoint
  async searchBlogPosts(query: string, page: number = 1, limit: number = 18): Promise<BlogResponse> {
    try {
      const response = await blogApi.get(`/blogs/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error searching blog posts:', error);
      throw error;
    }
  },

  // Get all blog categories
  async getBlogCategories(page: number = 1, limit: number = 10): Promise<BlogCategoryResponse> {
    try {
      const response = await blogApi.get(`/blogs/categories?page=${page}&limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching blog categories:', error);
      throw error;
    }
  },

  // Get blog category by slug with posts
  async getBlogCategoryBySlug(slug: string): Promise<BlogCategoryDetailResponse> {
    try {
      const response = await blogApi.get(`/blogs/categories/slug/${slug}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching blog category:', error);
      throw error;
    }
  },

  // Get more posts for a category with pagination
  async getCategoryPosts(slug: string, page: number = 1, limit: number = 12): Promise<BlogCategoryDetailResponse> {
    try {
      const response = await blogApi.get(`/blogs/categories/slug/${slug}?page=${page}&limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching category posts:', error);
      throw error;
    }
  }
};

export default blogService;
